import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Archive, Download, FileCode2, FileText, Loader2, Printer } from 'lucide-react';
import { api, type Pagina } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { CampoDeBusqueda } from '@/shared/ui/CampoDeBusqueda';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { Tabla, type Columna } from '@/shared/ui/Tabla';
import { notificar } from '@/shared/ui/avisos';
import type { FacturaListada } from './estadosDeFactura';

const TOPE = 200;

const QUE_LLEVA = [
  { valor: 'ambos', etiqueta: 'PDF y XML' },
  { valor: 'ride', etiqueta: 'Sólo el PDF' },
  { valor: 'xml', etiqueta: 'Sólo el XML' },
] as const;

export function PaginaDescargas() {
  const [buscar, setBuscar] = useState('');
  const [periodo, setPeriodo] = useState('');
  const [incluir, setIncluir] = useState<string>('ambos');
  const [pagina, setPagina] = useState(0);
  const [tamanio, setTamanio] = useState(25);
  const [bajando, setBajando] = useState(false);

  const facturas = useQuery({
    queryKey: ['descargas', buscar, periodo, pagina, tamanio],
    queryFn: () =>
      api.get<Pagina<FacturaListada>>('/facturas', {
        query: { buscar, periodo, situacionSri: 'autorizada', pagina, tamanio },
      }),
  });

  const recuento = useQuery({
    queryKey: ['descargas-cuantas', buscar, periodo],
    queryFn: () =>
      api.get<{ total: number; descargables: number; pasaDelTope: boolean }>(
        '/facturas/descarga/cuantas',
        { query: { buscar, periodo, situacionSri: 'autorizada' } },
      ),
  });

  const cuantas = recuento.data?.descargables ?? 0;
  const pasaDelTope = recuento.data?.pasaDelTope ?? false;

  async function descargar() {
    setBajando(true);
    try {
      const parametros = new URLSearchParams({ buscar, periodo, incluir });
      const fichero = await api.descargar(`/facturas/descarga.zip?${parametros}`);

      if (!fichero) {
        notificar.error('NO SE PUDO ARMAR LA DESCARGA');
        return;
      }

      const url = URL.createObjectURL(fichero);
      const enlace = document.createElement('a');
      enlace.href = url;
      enlace.download = `comprobantes-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(enlace);
      enlace.click();
      enlace.remove();
      URL.revokeObjectURL(url);
    } catch (causa) {
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'No se pudo descargar');
    } finally {
      setBajando(false);
    }
  }

  const columnas: Columna<FacturaListada>[] = [
    {
      clave: 'cliente',
      encabezado: 'Cliente',
      contenido: (f) => (
        <span className="block">
          <span className="block truncate font-medium text-armazon-700">{f.cliente}</span>
          <span className="block font-mono text-[10px] text-armazon-400">{f.identificacion}</span>
        </span>
      ),
    },
    { clave: 'periodo', encabezado: 'Período', contenido: (f) => f.periodo },
    {
      clave: 'numero',
      encabezado: 'Nº factura',
      contenido: (f) => <span className="font-mono text-xs">{f.numero}</span>,
    },
    {
      clave: 'total',
      encabezado: 'Total',
      alinear: 'derecha',
      contenido: (f) => <span className="importe">${f.total.toFixed(2)}</span>,
    },
    {
      clave: 'lleva',
      encabezado: 'Lleva',
      alinear: 'centro',

      contenido: (f) => (
        <span
          className="flex items-center justify-center gap-2 text-armazon-400"
          title={f.historica ? 'La emitió el sistema anterior' : undefined}
        >
          {f.historica && <Archive className="size-3 text-amber-600" aria-hidden />}
          {incluir !== 'xml' && <Printer className="size-3.5" aria-hidden />}
          {incluir !== 'ride' && <FileCode2 className="size-3.5" aria-hidden />}
        </span>
      ),
    },
  ];

  return (
    <>
      <EncabezadoDePagina
        icono={Archive}
        color="indigo"
        titulo="Descargas"
        subtitulo="Bajarse varios comprobantes de una vez, en un ZIP"
      />

      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-armazon-200 bg-white p-4">
        <span className="text-[11px] font-bold uppercase tracking-wide text-armazon-600">
          Qué se lleva
        </span>

        <div className="flex gap-1">
          {QUE_LLEVA.map((q) => (
            <button
              key={q.valor}
              type="button"
              onClick={() => setIncluir(q.valor)}
              aria-pressed={incluir === q.valor}
              className={`h-9 rounded-lg border px-3 text-[11px] font-semibold uppercase transition ${
                incluir === q.valor
                  ? 'border-marca-400 bg-marca-50 text-marca-700'
                  : 'border-armazon-200 bg-white text-armazon-500 hover:border-armazon-300'
              }`}
            >
              {q.etiqueta}
            </button>
          ))}
        </div>

        <input
          type="month"
          value={periodo}
          onChange={(evento) => {
            setPeriodo(evento.target.value);
            setPagina(0);
          }}
          aria-label="Período"
          className="h-9 rounded-lg border border-armazon-200 px-2 text-xs text-armazon-700 outline-none focus:border-marca-400"
        />

        <div className="ml-auto flex items-center gap-3">
          {pasaDelTope && (
            <span className="text-[10px] uppercase leading-tight text-amber-700">
              Se bajan los primeros {TOPE}: afine el filtro
            </span>
          )}
          <button
            type="button"
            onClick={() => void descargar()}
            disabled={bajando || cuantas === 0}
            className="inline-flex h-9 items-center gap-2 rounded-lg bg-marca-600 px-4 text-[11px] font-bold uppercase tracking-wide text-white transition hover:bg-marca-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {bajando ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Download className="size-4" aria-hidden />
            )}
            {bajando
              ? 'Armando el ZIP…'
              : `Descargar ${Math.min(cuantas, TOPE).toLocaleString('es-EC')}`}
          </button>
        </div>
      </div>

      <Tabla
        datos={facturas.data}
        cargando={facturas.isLoading}
        columnas={columnas}
        tamanio={tamanio}
        alCambiarPagina={setPagina}
        alCambiarTamanio={(nuevo) => {
          setTamanio(nuevo);
          setPagina(0);
        }}
        iconoVacio={FileText}
        mensajeVacio="Ninguna factura autorizada con ese filtro"
        entidad="AUTORIZADAS"
        herramientas={
          <CampoDeBusqueda
            valor={buscar}
            alCambiar={(texto) => {
              setBuscar(texto);
              setPagina(0);
            }}
            marcador="Nombre, cédula, nº de comprobante o clave de acceso"
          />
        }
      />
    </>
  );
}
